// ============================================================
// API Client - Taller Vargas ERP
// Centraliza todas las llamadas al backend REST
// ============================================================

function getBaseUrl() {
  const envUrl = import.meta.env.VITE_API_URL;
  // Si está explícitamente configurada una URL externa completa que no sea localhost, usarla
  if (envUrl && !envUrl.includes('localhost')) {
    return envUrl.replace(/\/+$/, '');
  }
  // En producción (Nginx) o si el navegador accede desde una IP/dominio remoto o local
  if (import.meta.env.PROD || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1')) {
    return ''; // Usa rutas relativas (/api), aprovechando Nginx o el proxy inverso
  }
  return envUrl || '';
}

export const BASE_URL = getBaseUrl();

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, options = {}) {
  const url = `${BASE_URL}/api${path}`;
  const headers = { 
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
    ...options.headers 
  };

  // Inyectar JWT Bearer Token si existe en localStorage
  const token = localStorage.getItem('vargas_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    headers,
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));

  // Si el token expiró o es inválido, redirigir al login
  if (res.status === 401 && path !== '/auth/login') {
    localStorage.removeItem('vargas_token');
    localStorage.removeItem('vargas_user');
    localStorage.removeItem('taller_mecanico_id');
    localStorage.removeItem('taller_mecanico_nombre');
    window.navigate && window.navigate('/login');
    throw new ApiError('Sesión expirada. Por favor vuelve a iniciar sesión.', 401);
  }

  if (res.status === 404) {
    console.warn(`[API 404] Recurso no encontrado: ${url}`);
  }

  if (!res.ok) throw new ApiError(data.error || 'Error de servidor', res.status);
  return data;
}

// ── Autenticación ───────────────────────────────────────
const loginRaw = (username, password) => request('/auth/login', {
  method: 'POST',
  body: { username, password }
});
export const login = loginRaw;
export const getMe = () => request('/auth/me');
export function logout() {
  localStorage.removeItem('vargas_token');
  localStorage.removeItem('vargas_user');
  localStorage.removeItem('taller_mecanico_id');
  localStorage.removeItem('taller_mecanico_nombre');
}

// ── Dashboard ────────────────────────────────────────────
export const getDashboard = () => request('/dashboard');

// ── Clientes ─────────────────────────────────────────────
export const getClientes          = ()         => request('/clientes');
export const getCliente           = (id)       => request(`/clientes/${id}`);
export const getClienteHistorial  = (id)       => request(`/clientes/${id}/historial`);
export const getClientesCrmStats  = ()         => request('/clientes/stats/crm');
export const createCliente        = (data)     => request('/clientes', { method: 'POST', body: data });
export const updateCliente        = (id, data) => request(`/clientes/${id}`, { method: 'PUT', body: data });
export const patchClienteNotas    = (id, nota) => request(`/clientes/${id}/notas`, { method: 'PATCH', body: { notas: nota } });
export const deleteCliente        = (id)       => request(`/clientes/${id}`, { method: 'DELETE' });

// ── Vehículos ─────────────────────────────────────────────
export const getVehiculos    = ()       => request('/vehiculos');
export const getHistorial    = (id)     => request(`/vehiculos/${id}/historial`);
export const createVehiculo  = (data)   => request('/vehiculos', { method: 'POST', body: data });
export const updateVehiculo  = (id, d)  => request(`/vehiculos/${id}`, { method: 'PUT', body: d });

// ── Mecánicos ─────────────────────────────────────────────
export const getMecanicos       = ()         => request('/mecanicos');
export const getMecanicosStats  = ()         => request('/mecanicos/stats');
export const createMecanico     = (data)     => request('/mecanicos', { method: 'POST', body: data });
export const updateMecanico     = (id, data) => request(`/mecanicos/${id}`, { method: 'PUT', body: data });
export const patchOrdenMecanico = (id, mid)  => request(`/ordenes/${id}/mecanico`, { method: 'PATCH', body: { mecanico_id: mid } });


// ── Órdenes de Servicio ──────────────────────────────────
export const getOrdenes           = (params = '') => request(`/ordenes${params ? (params.startsWith('?') ? params : `?${params}`) : ''}`);
export const getOrdenesActivas     = ()         => request('/ordenes?activas=true');
export const getOrdenesListos      = ()         => request('/ordenes?listos=true');
export const getOrdenesConteo      = ()         => request('/ordenes/conteo');
export const getOrdenesEnProceso   = ()         => request('/ordenes/proceso');
export const getOrden             = (id)       => request(`/ordenes/${id}`);
export const createOrden    = (data)     => request('/ordenes', { method: 'POST', body: data });
export const updateOrden    = (id, data) => request(`/ordenes/${id}`, { method: 'PUT', body: data });
export const cambiarEstado  = (id, data) => request(`/ordenes/${id}/estado`, { method: 'PATCH', body: data });
export const addItem        = (id, data) => request(`/ordenes/${id}/items`, { method: 'POST', body: data });
export const deleteItem     = (oid, iid) => request(`/ordenes/${oid}/items/${iid}`, { method: 'DELETE' });
export const guardarDiagnosticoOrden   = (id, diagnostico) => request(`/ordenes/${id}/diagnostico`, { method: 'PATCH', body: { diagnostico } });
export const patchNotaInternaOrden     = (id, nota_interna) => request(`/ordenes/${id}/nota-interna`, { method: 'PATCH', body: { nota_interna } });

// ── Almacén ───────────────────────────────────────────────
export const getAlmacen       = ()           => request('/almacen');
export const getCatalogoAlmacen = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  return request(`/almacen${search.toString() ? `?${search.toString()}` : ''}`);
};
export const createRepuesto = (data) => request('/almacen', { method: 'POST', body: data });
export const updateRepuesto = (id, data) => request(`/almacen/${id}`, { method: 'PUT', body: data });
export const ajustarStockRepuesto = (id, data) => request(`/almacen/${id}/stock`, { method: 'PATCH', body: data });
export const setRepuestoActivo = (id, activo) => request(`/almacen/${id}/activo`, { method: 'PATCH', body: { activo } });
export const getKardexRepuesto = (id) => request(`/almacen/${id}/kardex`);
export const getAlmacenMecanico = ()         => request('/almacen/mecanico');
export const getAlertasStock  = ()           => request('/almacen/alertas');
export const getSolicitudesMecanico = ()     => request('/almacen/solicitudes');
export const crearSolicitudMecanico = (data) => request('/almacen/solicitudes', { method: 'POST', body: data });
export const confirmarSolicitudMecanico = (id) => request(`/almacen/solicitudes/${id}/confirmar`, { method: 'PATCH' });
export const eliminarSolicitudMecanico = (id) => request(`/almacen/solicitudes/${id}`, { method: 'DELETE' });

// ── Facturación ───────────────────────────────────────────
export const getCobros    = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const queryString = search.toString();
  return request(`/cobros${queryString ? `?${queryString}` : ''}`);
};
export const getStatsCobros = ()       => request('/cobros/stats');
export const registrarCobro = (id, d)  => request(`/cobros/${id}/cobrar`, { method: 'PATCH', body: d });
export const dividirCobro   = (id, d)  => request(`/cobros/${id}/dividir`, { method: 'PATCH', body: d });
export const registrarAbono = (id, d)  => request(`/cobros/${id}/abono`, { method: 'POST', body: d });
export const getAbonosCobro = (id)     => request(`/cobros/${id}/abonos`);
export const crearVentaRapida = (data) => request('/cobros/venta-rapida', { method: 'POST', body: data });
export const exportarCobrosCSV = async () => {
  const token = localStorage.getItem('vargas_token');
  const headers = { 'Cache-Control': 'no-cache' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE_URL}/api/cobros/exportar`, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Error al exportar cobros');
  }
  return res.text();
};

// ── Archivos ──────────────────────────────────────────────
export const createArchivo  = (data)   => request('/archivos', { method: 'POST', body: data });

// ── Confirmación Pública del Cliente ──────────────────────
export const getOrdenPublica = (id) => request(`/ordenes/${id}/publica`);
export const confirmarOrdenPublica = (id) => request(`/ordenes/${id}/confirmar`, { method: 'POST' });

// ── Gestión de Usuarios ───────────────────────────────────
export const getUsuarios    = ()         => request('/usuarios');
export const createUsuario  = (data)     => request('/usuarios', { method: 'POST', body: data });
export const updateUsuario  = (id, data) => request(`/usuarios/${id}`, { method: 'PUT', body: data });
export const deleteUsuario  = (id)       => request(`/usuarios/${id}`, { method: 'DELETE' });

// ── Proformas / Cotizaciones ─────────────────────────────
export const getProformas             = ()         => request('/proformas');
export const getProforma              = (id)       => request(`/proformas/${id}`);
export const createProforma           = (data)     => request('/proformas', { method: 'POST', body: data });
export const updateProforma           = (id, data) => request(`/proformas`, { method: 'POST', body: { ...data, id } });
export const deleteProforma           = (id)       => request(`/proformas/${id}`, { method: 'DELETE' });
export const convertirProformaAOrden  = (id)       => request(`/proformas/${id}/convertir`, { method: 'POST' });

